import React, { useState, useEffect } from 'react';
import { Card, DatePicker, Button, Statistic, Row, Col, Table, message, Empty, Select } from 'antd';
import { DollarOutlined, ShoppingOutlined, CheckCircleOutlined, SearchOutlined, BarChartOutlined, UserOutlined } from '@ant-design/icons';
import { $api } from 'services/service';
import { IRevenueStatistics, ITicketTypeStatistics, formatCurrency } from '../ticketModels';
import moment from 'moment';
import 'moment/locale/vi';
import locale from 'antd/es/date-picker/locale/vi_VN';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface IUser {
    id: string;
    fullName: string;
    userName: string;
    role: number;
}

const getRoleName = (role: number): string => {
    switch (role) {
        case 99: return 'Admin';
        case 20: return 'Nhân viên căn teen';
        case 21: return 'Nhân viên giữ xe';
        case 22: return 'Nhân viên KTX';
        case 30: return 'Quản lý dịch vụ';
        default: return '';
        
    }
};

const RevenueStatistics: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState<any[]>([moment().startOf('month'), moment().endOf('month')]);
    const [statistics, setStatistics] = useState<IRevenueStatistics | null>(null);
    const [users, setUsers] = useState<IUser[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [userSearchText, setUserSearchText] = useState('');

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async (searchText: string = '') => {
        setLoadingUsers(true);
        try {
            const res: any = await $api.user.getPaging({
                freeTextSearch: searchText || undefined,
                roles: [20, 21, 22, 30, 99],
                pageIndex: 1,
                pageSize: 100
            });

            if (res.result && res.result.items) {
                setUsers(res.result.items);
            }
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleUserSearch = (value: string) => {
        setUserSearchText(value);
        if (value.length > 0) {
            loadUsers(value);
        }
    };

    const loadStatistics = async () => {
        if (!dateRange || dateRange.length !== 2) {
            message.warning('Vui lòng chọn khoảng thời gian');
            return;
        }

        setLoading(true);
        try {
            const res: any = await $api.ticket.getRevenue({
                startDate: dateRange[0].format('YYYY-MM-DD'),
                endDate: dateRange[1].format('YYYY-MM-DD'),
                userId: selectedUserId || undefined,
            });

            if (res.result) {
                setStatistics(res.result);
            }
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Không thể tải thống kê');
            setStatistics(null);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Mã</span>,
            dataIndex: 'ticketTypeCode',
            key: 'ticketTypeCode',
            width: 90,
            render: (code: string) => <span className="font-mono font-bold text-xs sm:text-base">{code}</span>,
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Tên loại phiếu</span>,
            dataIndex: 'ticketTypeName',
            align: 'left' as const,
            key: 'ticketTypeName',
            render: (name: string) => <span className="font-semibold text-xs sm:text-base">{name}</span>,
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Bán</span>,
            dataIndex: 'ticketsSold',
            key: 'ticketsSold',
            width: 80,
            align: 'center' as const,
            render: (count: number) => (
                <span className="font-bold text-blue-600 text-sm sm:text-lg">{count.toLocaleString()}</span>
            ),
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Dùng</span>,
            dataIndex: 'ticketsUsed',
            key: 'ticketsUsed',
            width: 80,
            align: 'center' as const,
            render: (count: number) => (
                <span className="font-bold text-green-600 text-sm sm:text-lg">{count.toLocaleString()}</span>
            ),
        },
        {
            title: <span className="text-sm sm:text-lg font-bold text-gray-800">Doanh thu</span>,
            dataIndex: 'revenue',
            key: 'revenue',
            width: 120,
            align: 'right' as const,
            render: (revenue: number) => (
                <span className="font-bold text-base sm:text-xl text-green-600">{formatCurrency(revenue)}</span>
            ),
        }
    ];

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Filter Section */}
            <Card className="shadow-md">
                <div className="space-y-3 sm:space-y-4">
                    <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
                        <BarChartOutlined />
                        <span className="hidden xs:inline">Chọn khoảng thời gian thống kê</span>
                        <span className="xs:hidden">Thống kê</span>
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                            <label className="block mb-1 sm:mb-2 text-xs sm:text-sm font-medium text-gray-700">Khoảng thời gian</label>
                            <RangePicker
                                value={dateRange}
                                onChange={(dates) => setDateRange(dates || [])}
                                format="DD/MM/YYYY"
                                size="middle"
                                placeholder={['Từ ngày', 'Đến ngày']}
                                className="w-full"
                                locale={locale}
                                defaultPickerValue={[dayjs().startOf('month'), dayjs().endOf('month')]}
                            />
                        </div>
                        <div>
                            <label className="block mb-1 sm:mb-2 text-xs sm:text-sm font-medium text-gray-700">Người bán</label>
                            <Select
                                size="middle"
                                placeholder="Chọn người bán"
                                allowClear
                                showSearch
                                value={selectedUserId}
                                onChange={setSelectedUserId}
                                onSearch={handleUserSearch}
                                loading={loadingUsers}
                                filterOption={false}
                                className="w-full"
                                suffixIcon={<UserOutlined />}
                            >
                                {users.map(user => (
                                    <Option key={user.id} value={user.id}>
                                        {user.fullName} ({getRoleName(user.role)})
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </div>

                    <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                        <Button
                            className="focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                            onClick={() => setDateRange([moment().startOf('day'), moment().endOf('day')])}>
                            Hôm nay
                        </Button>
                        <Button
                            className="focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                            onClick={() => setDateRange([moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')])}>
                            Hôm qua
                        </Button>
                        <Button
                            className="focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                            onClick={() => setDateRange([moment().startOf('week'), moment().endOf('week')])}>
                            Tuần này
                        </Button>
                        <Button
                            className="focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                            onClick={() => setDateRange([moment().startOf('month'), moment().endOf('month')])}>
                            Tháng này
                        </Button>
                        <Button
                            className="focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm hidden xs:inline-block"
                            onClick={() => setDateRange([moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')])}>
                            Tháng trước
                        </Button>
                    </div>

                    <Button
                        type="primary"
                        size="middle"
                        icon={<SearchOutlined />}
                        onClick={loadStatistics}
                        loading={loading}
                        className="w-full sm:w-auto px-6 sm:px-8 focus:outline-none focus:shadow-none active:shadow-none"
                    >
                        Xem thống kê
                    </Button>
                </div>
            </Card>

            {/* Statistics Cards */}
            {statistics ? (
                <>
                    <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12} lg={8}>
                            <Card className="shadow-md border-l-4 border-blue-500">
                                <Statistic
                                    title={<span className="text-sm sm:text-lg font-semibold">Tổng phiếu đã bán</span>}
                                    value={statistics.totalTicketsSold}
                                    prefix={<ShoppingOutlined className="text-blue-500" />}
                                    valueStyle={{ color: '#1890ff', fontSize: window.innerWidth < 640 ? '1.5rem' : '2rem', fontWeight: 'bold' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={8}>
                            <Card className="shadow-md border-l-4 border-green-500">
                                <Statistic
                                    title={<span className="text-sm sm:text-lg font-semibold">Tổng lần đã sử dụng</span>}
                                    value={statistics.totalTicketsUsed}
                                    prefix={<CheckCircleOutlined className="text-green-500" />}
                                    valueStyle={{ color: '#52c41a', fontSize: window.innerWidth < 640 ? '1.5rem' : '2rem', fontWeight: 'bold' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={24} lg={8}>
                            <Card className="shadow-md border-l-4 border-yellow-500">
                                <Statistic
                                    title={<span className="text-sm sm:text-lg font-semibold">Tổng doanh thu</span>}
                                    value={statistics.totalRevenue}
                                    prefix={<DollarOutlined className="text-yellow-600" />}
                                    valueStyle={{ color: '#faad14', fontSize: '2rem', fontWeight: 'bold' }}
                                    formatter={(value) => formatCurrency(Number(value))}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Detailed Table */}
                    <Card className="shadow-lg border-t-4 border-blue-500">
                        <h3 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-2">
                            <span className="text-blue-600">📊</span>
                            Chi tiết theo loại phiếu
                        </h3>
                        <Table
                            columns={columns}
                            dataSource={statistics.ticketTypeStatistics}
                            rowKey="ticketTypeId"
                            pagination={false}
                            scroll={{ x: 800 }}
                            size="large"
                            bordered
                            rowClassName={(record, index) =>
                                index % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-blue-50'
                            }
                            summary={(pageData) => {
                                let totalSold = 0;
                                let totalUsed = 0;
                                let totalRevenue = 0;

                                pageData.forEach(({ ticketsSold, ticketsUsed, revenue }) => {
                                    totalSold += ticketsSold;
                                    totalUsed += ticketsUsed;
                                    totalRevenue += revenue;
                                });

                                return (
                                    <Table.Summary fixed>
                                        <Table.Summary.Row className="bg-blue-50">
                                            <Table.Summary.Cell index={0} colSpan={2} className="py-6">
                                                <span className="font-bold text-xl text-gray-800">🎯 Tổng cộng</span>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={2} align="center" className="py-6">
                                                <span className="font-bold text-blue-600 text-2xl">
                                                    {totalSold.toLocaleString()}
                                                </span>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={3} align="center" className="py-6">
                                                <span className="font-bold text-green-600 text-2xl">
                                                    {totalUsed.toLocaleString()}
                                                </span>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4} align="right" className="py-6">
                                                <span className="font-bold text-green-600 text-2xl">
                                                    {formatCurrency(totalRevenue)}
                                                </span>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                );
                            }}
                        />
                    </Card>
                </>
            ) : (
                <Card className="shadow-md">
                    <Empty
                        description="Chọn khoảng thời gian và nhấn 'Xem thống kê' để hiển thị dữ liệu"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                </Card>
            )}
        </div>
    );
};

export default RevenueStatistics;
